import { Fragment, useState, type ReactNode } from "react";
import { Trash2 } from "lucide-react";
import { yuvarla3, type KutukBelgeYaniti, type PaketDetayi, type PaketSatiri, type StokPnSatiri, type StokYaniti } from "@oasis/domain";
import { api } from "@/lib/api";
import { cn } from "@/lib/cn";
import { cevir } from "@/lib/dil";
import { hareketTuruMetni, kaldirmaNedeniMetni, kullanimMetni } from "@/lib/stok-paket";
import { tarih, tarihSaat, useVeri } from "@/lib/veri";
import { Bos, Buton, Hata, Kutu, Modal, alan, etiket, girdi, tabloBas, tabloHucre } from "@/components/parcalar";
import { BelgeCipleri, KutukBelgeleri } from "@/components/kutuk-belgeleri";
import { GRI_ROZET, YUVA, bugun, farkMetni, gosterDugmesiSinifi, gunGirdisi, miktarMetni, sayiOku, taslakGirdisi, taslakTamam, type BelgeTaslagi } from "@/components/kutuk-kullanim";

/**
 * D-045 / D-046 / D-048 / D-053: Registers → Stock. PN kataloğu (PN satırı) ve altında her gelişin ayrı paketi (girintili satır);
 * "Add package", paket penceresi (Quantity history, Used in, Documents), "Enter stock count", "Mark finished", "Clear opening stock".
 * Seçimler miktardan düşmez; miktar yalnız sayım, Edit ve Mark finished ile değişir. Biten paket (kalan 0) PN içinde sona düşer ve gizlidir.
 * Açılış stoğu (paketlerden önce girilmiş miktar) pakete çevrilmez: PN satırındaki "Clear opening stock" ile sıfırlanır (D-053).
 * Şartname: sayfalar/kutukler--stok-ilk-gun, --stok-paket, --stok-paket-form, --stok-sayim.
 */

/** PN formu alanı (GET /kutuk/stok `alanlar`: pn, tanim, birim). */
interface PnAlani { ad: string; etiket: string; tip: string; zorunlu?: boolean; secenek?: string[] }

/** "Add package" ve paket penceresinde PN listesinin sonundaki "New PN…" seçeneğinin değeri. */
const YENI_PN = "__yeni_pn__";
const BIRIMLER = ["ad", "m", "kg"];

/** "New PN…" seçilince açılan PN / Definition / Unit alanları. */
type YeniPn = { pn: string; tanim: string; birim: string };

const hataMetni = (e: unknown) => (e instanceof Error ? e.message : cevir("Error"));
const bos = <span className="text-ink-3">—</span>;
const pnSecenegi = (p: StokPnSatiri) => (p.tanim ? `${p.pn} — ${p.tanim}` : p.pn);

/** Paketin geliş bilgisi: eski açılış paketinde (D-053'ten beri üretilmez) "opening stock", gelişte "arrived {0}". */
const gelisMetni = (k: PaketSatiri) => (k.kaynak === "acilis" ? cevir("opening stock") : k.gelisTarihi ? cevir("arrived {0}", tarih(k.gelisTarihi)) : null);

export function StokSekmesi({ onDegisti }: { onDegisti: () => void }) {
  const { veri, hata, yenile } = useVeri<StokYaniti>("/kutuk/stok");
  const [bitenAcik, setBitenAcik] = useState(false);
  const [islem, setIslem] = useState<string | null>(null);
  const [pnForm, setPnForm] = useState<{ pn: string; veri: Record<string, string> } | null>(null);
  const [ekle, setEkle] = useState<{ pn: string | null } | null>(null);
  const [paket, setPaket] = useState<{ id: string; surum: number } | null>(null);
  const [bitir, setBitir] = useState<{ id: string; pkg: string } | null>(null);
  const [sifirla, setSifirla] = useState<{ pn: string; miktar: string } | null>(null);
  const [sayim, setSayim] = useState(false);

  if (hata) return <Hata mesaj={hata} />;
  if (!veri) return <Bos>{cevir("Loading...")}</Bos>;

  const alanlar = veri.alanlar as PnAlani[];
  const birimler = alanlar.find((a) => a.ad === "birim")?.secenek ?? BIRIMLER;

  const tazele = async () => {
    await yenile();
    onDegisti();
  };
  const dene = async (f: () => Promise<unknown>) => {
    setIslem(null);
    try {
      await f();
      await tazele();
    } catch (e) {
      setIslem(hataMetni(e));
    }
  };

  const pnSil = (pn: string) => {
    if (window.confirm(cevir("Delete {0}? It is not used anywhere. This cannot be undone.", pn))) void dene(() => api.sil(`/kutuk/stok/${encodeURIComponent(pn)}`));
  };
  const paketSil = (k: PaketSatiri) => {
    if (window.confirm(cevir("Delete {0}? It is not used anywhere. This cannot be undone; attached PDFs stay in OASIS.", k.pkg))) void dene(() => api.sil(`/kutuk/stok/paket/${k.id}`));
  };
  const pnDuzenle = (p: StokPnSatiri) => {
    const kayit: Record<string, unknown> = { pn: p.pn, tanim: p.tanim, birim: p.birim };
    setIslem(null);
    setPnForm({ pn: p.pn, veri: Object.fromEntries(alanlar.map((a) => [a.ad, kayit[a.ad] === null || kayit[a.ad] === undefined ? "" : String(kayit[a.ad])])) });
  };

  const paketVar = veri.pnler.some((p) => p.paketler.length > 0);

  /** PN satırının "Packages" hücresi: paket özeti ve pakete girmemiş açılış stoğu (altında D-053 "Clear opening stock" bağlantısı). */
  const ozet = (p: StokPnSatiri): ReactNode => {
    const acilisMiktari = `${miktarMetni(p.acilis)} ${p.birim}`;
    /** Açılış metni kendi satırında, bağlantı her zaman altında: yan yana yazılırsa Packages kolonu genişler, Definition daralır. */
    const acilisSatiri = (metin: string) => (
      <>
        <span className="block whitespace-nowrap text-xs text-ink-3">{metin}</span>
        <button type="button" className="whitespace-nowrap text-xs text-ink-3 underline hover:text-ink" onClick={() => { setIslem(null); setSifirla({ pn: p.pn, miktar: acilisMiktari }); }}>
          {cevir("Clear opening stock")}
        </button>
      </>
    );
    if (p.paketSayisi === 0 && p.acilis > 0) return acilisSatiri(cevir("no package yet · opening stock: {0}", acilisMiktari));
    return (
      <>
        {p.aktifPaket > 0 ? (
          <span className="block whitespace-nowrap text-xs text-ink-2">{p.aktifPaket === 1 ? cevir("1 active package") : cevir("{0} active packages", p.aktifPaket)}</span>
        ) : (
          <span className="block whitespace-nowrap text-xs text-ink-3">{cevir("no active package")}</span>
        )}
        {p.acilis > 0 && acilisSatiri(cevir("opening stock: {0} · not in a package", acilisMiktari))}
      </>
    );
  };

  const paketSatiri = (k: PaketSatiri) => (
    <tr key={k.id} className={cn("border-b border-line bg-surface-2/60", k.bitti && "text-ink-3", k.bitti && !bitenAcik && "hidden")}>
      <td className={tabloHucre}><span className="ml-4 whitespace-nowrap font-mono">{k.pkg}</span></td>
      {k.kaynak === "acilis" ? (
        <td className={cn(tabloHucre, "text-ink-3")}>{cevir("opening stock")}</td>
      ) : (
        <td className={cn(tabloHucre, "whitespace-nowrap tnum")}>{k.gelisTarihi ? tarih(k.gelisTarihi) : bos}</td>
      )}
      <td className={cn(tabloHucre, "whitespace-nowrap")}>{k.iz || bos}</td>
      <td className={cn(tabloHucre, "whitespace-nowrap")}>{k.poNo || bos}</td>
      <td className={cn(tabloHucre, "tnum")}>{miktarMetni(k.gelenMiktar)}</td>
      <td className={cn(tabloHucre, "tnum")}>
        {miktarMetni(k.miktar)}
        {k.bitti && (
          <span className="mt-1 block">
            <span className={cn(GRI_ROZET, "whitespace-nowrap")}>{k.bitis ? cevir("finished · {0} · {1}", tarih(k.bitis.ts), k.bitis.kim) : cevir("finished")}</span>
          </span>
        )}
      </td>
      <td className={tabloHucre} />
      <td className={tabloHucre}><BelgeCipleri kutuk="stok_paket" belgeler={k.belgeler} poNo={k.poNo} /></td>
      <td className={tabloHucre}>
        <span className="flex items-center justify-end gap-1.5 whitespace-nowrap">
          <Buton kucuk onClick={() => { setIslem(null); setPaket({ id: k.id, surum: 0 }); }}>{cevir("Edit")}</Buton>
          {!k.bitti && <Buton kucuk onClick={() => { setIslem(null); setBitir({ id: k.id, pkg: k.pkg }); }}>{cevir("Mark finished")}</Buton>}
          <span className={YUVA}>
            {k.kullanim.toplam === 0 ? (
              <Buton kucuk tur="tehlike" title={cevir("Delete")} onClick={() => paketSil(k)}><Trash2 size={13} /></Buton>
            ) : (
              <span className="text-xs text-ink-3">{cevir("used in {0}", k.kullanim.etkin)}</span>
            )}
          </span>
        </span>
      </td>
    </tr>
  );

  return (
    <>
      <Hata mesaj={islem} />
      <Kutu
        baslik={cevir(veri.ad)}
        padded={false}
        aksiyon={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Buton kucuk onClick={() => { setIslem(null); setSayim(true); }}>{cevir("Enter stock count")}</Buton>
            <Buton kucuk tur="birincil" onClick={() => { setIslem(null); setEkle({ pn: null }); }}>{cevir("+ Add package")}</Buton>
          </div>
        }
      >
        <p className="border-b border-line px-4 py-2 text-sm text-ink-2">{cevir(veri.aciklama)}</p>
        <div className="p-4">
          <div className="scroll-thin overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead>
                <tr className="border-b border-line">
                  <th className={tabloBas}>PN</th>
                  <th colSpan={4} className={tabloBas}>{cevir("Definition")}</th>
                  <th className={tabloBas}>{cevir("In stock")}</th>
                  <th className={tabloBas}>{cevir("Unit")}</th>
                  <th className={tabloBas}>{cevir("Packages")}</th>
                  <th className={tabloBas} />
                </tr>
                {paketVar && (
                  <tr className="border-b border-line bg-surface-2/60">
                    <th className={tabloBas}><span className="ml-4">{cevir("Package")}</span></th>
                    <th className={tabloBas}>{cevir("Arrival")}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-ink-3">{cevir("Heat / SN / lot")}</th>
                    <th className={tabloBas}>{cevir("PO no.")}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-ink-3">{cevir("Received quantity")}</th>
                    <th className={tabloBas}>{cevir("Remaining")}</th>
                    <th className={tabloBas} />
                    <th className={cn(tabloBas, "w-44")}>{cevir("Documents")}</th>
                    <th className={tabloBas} />
                  </tr>
                )}
              </thead>
              <tbody>
                {veri.pnler.map((p) => (
                  <Fragment key={p.pn}>
                    <tr className="border-b border-line">
                      <td className={cn(tabloHucre, "whitespace-nowrap font-mono font-semibold")}>{p.pn}</td>
                      <td colSpan={4} className={tabloHucre}>{p.tanim ?? "—"}</td>
                      <td className={cn(tabloHucre, "tnum", p.stokta === 0 && "text-ink-3")}>{miktarMetni(p.stokta)}</td>
                      <td className={tabloHucre}>{p.birim}</td>
                      <td className={tabloHucre}>{ozet(p)}</td>
                      <td className={tabloHucre}>
                        <span className="flex items-center justify-end gap-1.5 whitespace-nowrap">
                          <Buton kucuk onClick={() => { setIslem(null); setEkle({ pn: p.pn }); }}>{cevir("+ Add package")}</Buton>
                          <Buton kucuk onClick={() => pnDuzenle(p)}>{cevir("Edit")}</Buton>
                          <span className={YUVA}>
                            {p.silinebilir ? (
                              <Buton kucuk tur="tehlike" title={cevir("Delete")} onClick={() => pnSil(p.pn)}><Trash2 size={13} /></Buton>
                            ) : p.kullanim.toplam > 0 ? (
                              <span className="text-xs text-ink-3">{kullanimMetni(p.kullanim)}</span>
                            ) : null}
                          </span>
                        </span>
                      </td>
                    </tr>
                    {[...p.paketler.filter((k) => !k.bitti), ...p.paketler.filter((k) => k.bitti)].map(paketSatiri)}
                  </Fragment>
                ))}
                {!veri.pnler.length && (
                  <tr><td colSpan={9}><Bos>{cevir("No stock items. Start with \"+ Add package\".")}</Bos></td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="mt-3">
            <button type="button" aria-pressed={bitenAcik} disabled={!veri.bitenSayisi} onClick={() => setBitenAcik(!bitenAcik)} className={gosterDugmesiSinifi(bitenAcik)}>
              {cevir("Show finished ({0})", veri.bitenSayisi)}
            </button>
          </div>
        </div>
      </Kutu>

      {pnForm && (
        <PnPenceresi
          baslik={`${cevir(veri.ad)} — ${pnForm.pn}`}
          alanlar={alanlar}
          ilk={pnForm}
          onKapat={() => setPnForm(null)}
          onKaydedildi={() => { setPnForm(null); void tazele(); }}
        />
      )}
      {ekle && (
        <EklePenceresi
          pn={ekle.pn}
          pnler={veri.pnler}
          birimler={birimler}
          onKapat={() => setEkle(null)}
          onKaydedildi={() => { setEkle(null); void tazele(); }}
        />
      )}
      {paket && (
        <PaketPenceresi
          key={`${paket.id}-${paket.surum}`}
          id={paket.id}
          pnler={veri.pnler}
          birimler={birimler}
          // "Mark finished" onayı açıkken paket penceresi kilitli: tek Esc iki pencereyi birden kapatmasın
          kilitli={Boolean(bitir)}
          onKapat={() => setPaket(null)}
          onDegisti={() => void tazele()}
          onBitir={(pkg) => setBitir({ id: paket.id, pkg })}
        />
      )}
      {bitir && (
        <BitirPenceresi
          pkg={bitir.pkg}
          id={bitir.id}
          onKapat={() => setBitir(null)}
          onBitti={() => {
            setBitir(null);
            // Pencereden işaretlendiyse paket penceresi taze veriyle yeniden açılır
            setPaket((p) => (p && p.id === bitir.id ? { id: p.id, surum: p.surum + 1 } : p));
            void tazele();
          }}
        />
      )}
      {sifirla && (
        <AcilisSifirlaPenceresi pn={sifirla.pn} miktar={sifirla.miktar} onKapat={() => setSifirla(null)} onSifirlandi={() => { setSifirla(null); void tazele(); }} />
      )}
      {sayim && (
        <SayimPenceresi pnler={veri.pnler} onKapat={() => setSayim(false)} onKaydedildi={() => { setSayim(false); void tazele(); }} />
      )}
    </>
  );
}

/** PN formu (Edit): yalnız PN (kapalı), Definition ve Unit (D-045); değişiklik PN'in bütün paketlerine uygulanır. */
function PnPenceresi({ baslik, alanlar, ilk, onKapat, onKaydedildi }: { baslik: string; alanlar: PnAlani[]; ilk: { pn: string; veri: Record<string, string> }; onKapat: () => void; onKaydedildi: () => void }) {
  const [veri, setVeri] = useState(ilk.veri);
  const [hata, setHata] = useState<string | null>(null);
  const [mesgul, setMesgul] = useState(false);
  const kaydet = async () => {
    setHata(null);
    setMesgul(true);
    try {
      await api.put(`/kutuk/stok/${encodeURIComponent(ilk.pn)}`, { veri });
      onKaydedildi();
    } catch (e) {
      setHata(hataMetni(e));
    } finally {
      setMesgul(false);
    }
  };
  return (
    <Modal baslik={baslik} onKapat={onKapat} genis>
      <Hata mesaj={hata} />
      <div className="grid gap-3 sm:grid-cols-2">
        {alanlar.map((a) => (
          <div key={a.ad}>
            <label className={etiket}>{cevir(a.etiket)}{a.zorunlu && <span className="text-bad"> *</span>}</label>
            {a.secenek ? (
              <select className={girdi} value={veri[a.ad] ?? ""} onChange={(e) => setVeri({ ...veri, [a.ad]: e.target.value })}>
                <option value="">—</option>
                {a.secenek.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : (
              <input className={girdi} disabled={a.ad === "pn"} value={veri[a.ad] ?? ""} onChange={(e) => setVeri({ ...veri, [a.ad]: e.target.value })} />
            )}
          </div>
        ))}
      </div>
      <p className="mt-2 text-xs text-ink-3">{cevir("Definition and unit belong to the PN; a change applies to all its packages.")}</p>
      <div className="mt-4 flex gap-2">
        <Buton tur="birincil" mesgul={mesgul} onClick={kaydet}>{cevir("Save")}</Buton>
        <Buton onClick={onKapat}>{cevir("Cancel")}</Buton>
      </div>
    </Modal>
  );
}

/** "New PN…" alanları ("Add package" ve paket penceresi): PN, Definition, Unit; üçü de zorunlu. */
function YeniPnAlanlari({ yeni, setYeni, birimler }: { yeni: YeniPn; setYeni: (y: YeniPn) => void; birimler: string[] }) {
  return (
    <div className="sm:col-span-2">
      <div className="grid gap-3 sm:grid-cols-3">
        <div>
          <label className={etiket}>PN<span className="text-bad"> *</span></label>
          <input className={girdi} autoFocus value={yeni.pn} onChange={(e) => setYeni({ ...yeni, pn: e.target.value })} />
        </div>
        <div>
          <label className={etiket}>{cevir("Definition")}<span className="text-bad"> *</span></label>
          <input className={girdi} value={yeni.tanim} onChange={(e) => setYeni({ ...yeni, tanim: e.target.value })} />
        </div>
        <div>
          <label className={etiket}>{cevir("Unit")}<span className="text-bad"> *</span></label>
          <select className={girdi} value={yeni.birim} onChange={(e) => setYeni({ ...yeni, birim: e.target.value })}>
            {birimler.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
      </div>
    </div>
  );
}

/** "Add package": PN katalogdan (sonda "New PN…"), Arrival date (bugün), Received quantity (zorunlu, boş açılır), Heat / SN / lot, PO no., Note, Documents. */
function EklePenceresi({ pn, pnler, birimler, onKapat, onKaydedildi }: { pn: string | null; pnler: StokPnSatiri[]; birimler: string[]; onKapat: () => void; onKaydedildi: () => void }) {
  const [secilen, setSecilen] = useState(pn ?? "");
  const [yeni, setYeni] = useState<YeniPn>({ pn: "", tanim: "", birim: birimler[0] ?? "ad" });
  const [f, setF] = useState({ gelisTarihi: bugun(), gelen: "", iz: "", poNo: "", not: "" });
  const [taslaklar, setTaslaklar] = useState<BelgeTaslagi[]>([]);
  const [hata, setHata] = useState<string | null>(null);
  const [mesgul, setMesgul] = useState(false);
  /** Documents bölümünde yükleme sürerken kaydedilmez ve kapanmaz. */
  const [belgeMesgul, setBelgeMesgul] = useState(false);
  const yeniMi = secilen === YENI_PN;
  const birim = yeniMi ? yeni.birim : pnler.find((p) => p.pn === secilen)?.birim ?? "";

  const kaydet = async () => {
    setHata(null);
    setMesgul(true);
    try {
      await api.post<PaketDetayi>("/kutuk/stok/paket", {
        ...(yeniMi ? { yeniPn: { pn: yeni.pn.trim(), tanim: yeni.tanim.trim(), birim: yeni.birim } } : secilen ? { pn: secilen } : {}),
        gelisTarihi: f.gelisTarihi || null,
        gelenMiktar: sayiOku(f.gelen),
        iz: f.iz.trim() || null,
        poNo: f.poNo.trim() || null,
        not: f.not.trim() || null,
        belgeler: taslaklar.filter(taslakTamam).map(taslakGirdisi),
      });
      onKaydedildi();
    } catch (e) {
      setHata(hataMetni(e));
    } finally {
      setMesgul(false);
    }
  };

  return (
    <Modal baslik={pn ? cevir("Add package — {0}", pn) : cevir("Add package")} onKapat={onKapat} genis kilitli={mesgul || belgeMesgul}>
      <Hata mesaj={hata} />
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label className={etiket}>PN<span className="text-bad"> *</span></label>
          <select className={girdi} value={secilen} onChange={(e) => setSecilen(e.target.value)}>
            <option value="">{cevir("Select...")}</option>
            {pnler.map((p) => <option key={p.pn} value={p.pn}>{pnSecenegi(p)}</option>)}
            <option value={YENI_PN}>{cevir("New PN…")}</option>
          </select>
        </div>
        {yeniMi && <YeniPnAlanlari yeni={yeni} setYeni={setYeni} birimler={birimler} />}
        <div>
          <label className={etiket}>{cevir("Arrival date")}</label>
          <input className={girdi} type="date" value={f.gelisTarihi} onChange={(e) => setF({ ...f, gelisTarihi: e.target.value })} />
        </div>
        <div>
          <label className={etiket}>{cevir("Heat / SN / lot")}</label>
          <input className={girdi} value={f.iz} onChange={(e) => setF({ ...f, iz: e.target.value })} />
        </div>
        <div>
          <label className={etiket}>{cevir("PO no.")}</label>
          <input className={girdi} value={f.poNo} onChange={(e) => setF({ ...f, poNo: e.target.value })} />
        </div>
        <div>
          <label className={etiket}>{cevir("Note")}</label>
          <input className={girdi} placeholder={cevir("optional")} value={f.not} onChange={(e) => setF({ ...f, not: e.target.value })} />
        </div>
        <div className="sm:col-span-2">
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <label className={etiket}>{cevir("Received quantity")}<span className="text-bad"> *</span></label>
              <input className={girdi} type="number" min={0} step="any" value={f.gelen} onChange={(e) => setF({ ...f, gelen: e.target.value })} />
            </div>
            <div>
              <label className={etiket}>{cevir("Unit")}</label>
              <input className={girdi} disabled value={birim} />
            </div>
          </div>
        </div>
      </div>
      <p className="mt-2 text-xs text-ink-3">{cevir("One heat / lot per package; two heats in one delivery are two packages with the same PO.")}</p>
      <KutukBelgeleri kutuk="stok_paket" anahtar={null} belgeler={[]} yenisiGelenBelgeler={[]} kaldirilanBelgeler={[]} taslaklar={taslaklar} onTaslak={setTaslaklar} kilit={mesgul} onMesgul={setBelgeMesgul} />
      <div className="mt-4 flex gap-2">
        <Buton tur="birincil" mesgul={mesgul} disabled={belgeMesgul} onClick={kaydet}>{cevir("Save")}</Buton>
        <Buton disabled={mesgul || belgeMesgul} onClick={onKapat}>{cevir("Cancel")}</Buton>
      </div>
    </Modal>
  );
}

/**
 * Paket penceresi (Edit): GET /kutuk/stok/paket/:id; alanlar, Documents (kayıtlı mod), Quantity history, Used in. `kilitli`: üstünde
 * "Mark finished" onayı açık (Esc ve dış tıklama bu pencereyi kapatmaz).
 */
function PaketPenceresi({
  id, pnler, birimler, kilitli, onKapat, onDegisti, onBitir,
}: { id: string; pnler: StokPnSatiri[]; birimler: string[]; kilitli: boolean; onKapat: () => void; onDegisti: () => void; onBitir: (pkg: string) => void }) {
  const { veri: d, setVeri, hata } = useVeri<PaketDetayi>(`/kutuk/stok/paket/${id}`);
  if (!d) {
    return (
      <Modal baslik={cevir("Package")} onKapat={onKapat} genis>
        {hata ? <Hata mesaj={hata} /> : <Bos>{cevir("Loading...")}</Bos>}
      </Modal>
    );
  }
  return (
    <PaketFormu
      d={d}
      pnler={pnler}
      birimler={birimler}
      kilitli={kilitli}
      onKapat={onKapat}
      onBitir={() => onBitir(d.paket.pkg)}
      onBelge={(y) => {
        setVeri((v) => (v ? { ...v, paket: { ...v.paket, belgeler: y.belgeler, yenisiGelenBelgeler: y.yenisiGelenBelgeler, kaldirilanBelgeler: y.kaldirilanBelgeler } } : v));
        onDegisti();
      }}
      onKaydedildi={() => {
        onDegisti();
        onKapat();
      }}
    />
  );
}

/**
 * Paket penceresinin formu. PN listesinin sonunda "New PN…": kullanılmamış paket katalogda olmayan PN'e taşınır (sunucu katalog satırını
 * açılış stoğu 0 ile açar). Kaydedilmemiş değişiklik varken "Mark finished" kapalı: onaydan sonra pencere taze veriyle yeniden kurulur ve
 * değişiklik sessizce kaybolurdu. Documents bölümünde yükleme sürerken Save / Cancel kapalı, pencere kilitli.
 */
function PaketFormu({
  d, pnler, birimler, kilitli, onKapat, onBitir, onBelge, onKaydedildi,
}: {
  d: PaketDetayi; pnler: StokPnSatiri[]; birimler: string[]; kilitli: boolean; onKapat: () => void; onBitir: () => void; onBelge: (y: KutukBelgeYaniti) => void; onKaydedildi: () => void;
}) {
  const p = d.paket;
  const ilk = { pn: p.pn, gelisTarihi: gunGirdisi(p.gelisTarihi), iz: p.iz ?? "", poNo: p.poNo ?? "", not: p.not ?? "", gelen: miktarMetni(p.gelenMiktar), kalan: miktarMetni(p.miktar) };
  const [f, setF] = useState(ilk);
  const [yeni, setYeni] = useState<YeniPn>({ pn: "", tanim: "", birim: d.stok.birim });
  const [taslaklar, setTaslaklar] = useState<BelgeTaslagi[]>([]);
  const [hata, setHata] = useState<string | null>(null);
  const [mesgul, setMesgul] = useState(false);
  const [belgeMesgul, setBelgeMesgul] = useState(false);
  const yeniMi = f.pn === YENI_PN;
  const birim = yeniMi ? yeni.birim : pnler.find((x) => x.pn === f.pn)?.birim ?? d.stok.birim;
  const pnListesi = pnler.some((x) => x.pn === p.pn) ? pnler : [{ pn: p.pn, tanim: d.stok.tanim } as StokPnSatiri, ...pnler];
  const kaydedilmemis = (Object.keys(ilk) as (keyof typeof ilk)[]).some((k) => f[k] !== ilk[k]) || taslaklar.length > 0;

  const kaydet = async () => {
    setHata(null);
    setMesgul(true);
    try {
      // Yalnız değişen alanlar gönderilir (gönderilmeyen alan değişmez; boş metin sunucuda null olur)
      const govde: Record<string, unknown> = {};
      if (yeniMi) govde.yeniPn = { pn: yeni.pn.trim(), tanim: yeni.tanim.trim(), birim: yeni.birim };
      else if (f.pn !== ilk.pn) govde.pn = f.pn;
      if (f.gelisTarihi !== ilk.gelisTarihi) govde.gelisTarihi = f.gelisTarihi || null;
      for (const k of ["iz", "poNo", "not"] as const) if (f[k].trim() !== ilk[k]) govde[k] = f[k].trim();
      const gelen = sayiOku(f.gelen);
      if (gelen === null || gelen !== yuvarla3(p.gelenMiktar)) govde.gelenMiktar = gelen;
      const kalan = sayiOku(f.kalan);
      if (kalan === null || kalan !== yuvarla3(p.miktar)) govde.miktar = kalan;
      if (Object.keys(govde).length) await api.put<PaketDetayi>(`/kutuk/stok/paket/${p.id}`, govde);
      // Tamamlanmış belge taslakları da eklenir; eklenen taslak listeden düşer (hata olursa kalanlar pencerede kalır)
      for (const t of taslaklar.filter(taslakTamam)) {
        onBelge(await api.post<KutukBelgeYaniti>("/kutuk/belge", { kutuk: "stok_paket", anahtar: p.id, ...taslakGirdisi(t) }));
        setTaslaklar((l) => l.filter((x) => x.kimlik !== t.kimlik));
      }
      onKaydedildi();
    } catch (e) {
      setHata(hataMetni(e));
    } finally {
      setMesgul(false);
    }
  };

  return (
    <Modal baslik={cevir("Package {0} — {1}", p.pkg, p.pn)} onKapat={onKapat} genis kilitli={mesgul || belgeMesgul || kilitli}>
      <Hata mesaj={hata} />
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label className={etiket}>PN<span className="text-bad"> *</span></label>
          <select className={girdi} disabled={!d.pnDegisebilir} value={f.pn} onChange={(e) => setF({ ...f, pn: e.target.value })}>
            {pnListesi.map((x) => <option key={x.pn} value={x.pn}>{pnSecenegi(x)}</option>)}
            <option value={YENI_PN}>{cevir("New PN…")}</option>
          </select>
          {!d.pnDegisebilir && <p className="mt-1 text-xs text-ink-3">{cevir("PN can be changed only while the package is not used.")}</p>}
        </div>
        {yeniMi && <YeniPnAlanlari yeni={yeni} setYeni={setYeni} birimler={birimler} />}
        <div>
          <label className={etiket}>{cevir("Arrival date")}</label>
          <input className={girdi} type="date" value={f.gelisTarihi} onChange={(e) => setF({ ...f, gelisTarihi: e.target.value })} />
        </div>
        <div>
          <label className={etiket}>{cevir("Heat / SN / lot")}</label>
          <input className={girdi} value={f.iz} onChange={(e) => setF({ ...f, iz: e.target.value })} />
        </div>
        <div>
          <label className={etiket}>{cevir("PO no.")}</label>
          <input className={girdi} value={f.poNo} onChange={(e) => setF({ ...f, poNo: e.target.value })} />
        </div>
        <div>
          <label className={etiket}>{cevir("Note")}</label>
          <input className={girdi} placeholder={cevir("optional")} value={f.not} onChange={(e) => setF({ ...f, not: e.target.value })} />
        </div>
        <div className="sm:col-span-2">
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <label className={etiket}>{cevir("Received quantity")}<span className="text-bad"> *</span></label>
              <input className={girdi} type="number" min={0} step="any" value={f.gelen} onChange={(e) => setF({ ...f, gelen: e.target.value })} />
            </div>
            <div>
              <label className={etiket}>{cevir("Remaining")}</label>
              <input className={girdi} type="number" min={0} step="any" value={f.kalan} onChange={(e) => setF({ ...f, kalan: e.target.value })} />
            </div>
            <div>
              <label className={etiket}>{cevir("Unit")}</label>
              <input className={girdi} disabled value={birim} />
            </div>
          </div>
        </div>
      </div>
      <p className="mt-2 text-xs text-ink-3">{cevir("One heat / lot per package; two heats in one delivery are two packages with the same PO.")}</p>

      <KutukBelgeleri
        kutuk="stok_paket"
        anahtar={p.id}
        belgeler={p.belgeler}
        yenisiGelenBelgeler={p.yenisiGelenBelgeler}
        kaldirilanBelgeler={p.kaldirilanBelgeler}
        taslaklar={taslaklar}
        onTaslak={setTaslaklar}
        onDegisti={onBelge}
        kilit={mesgul}
        onMesgul={setBelgeMesgul}
      />

      <div className="mt-4">
        <label className={etiket}>{cevir("Quantity history")}</label>
        {d.hareketler.length ? (
          <div className="scroll-thin overflow-x-auto">
            <table className="w-full min-w-[640px]">
              <thead>
                <tr className="border-b border-line">
                  {["Date", "By", "Type", "Before → after", "Difference", "Note"].map((h) => <th key={h} className={tabloBas}>{cevir(h)}</th>)}
                </tr>
              </thead>
              <tbody>
                {d.hareketler.map((h) => {
                  const fark = h.fark ?? (h.onceki === null ? h.yeni : null);
                  return (
                    <tr key={h.id} className="border-b border-line last:border-0">
                      <td className={cn(tabloHucre, "whitespace-nowrap tnum")}>{tarihSaat(h.ts)}</td>
                      <td className={cn(tabloHucre, "whitespace-nowrap")}>{h.kim}</td>
                      <td className={tabloHucre}>{hareketTuruMetni(h.tur)}</td>
                      <td className={cn(tabloHucre, "whitespace-nowrap tnum")}>{`${miktarMetni(h.onceki)} → ${miktarMetni(h.yeni)} ${d.stok.birim}`}</td>
                      <td className={cn(tabloHucre, "whitespace-nowrap tnum")}>{fark === null ? "—" : `${farkMetni(fark)} ${d.stok.birim}`}</td>
                      {/* Yalnız sistemin yazdığı notlar (açılış, geliş) çevrilir; sayım ve "finished" notu kullanıcının yazdığı metin */}
                      <td className={cn(tabloHucre, !h.not && "text-ink-3")}>{h.not ? (h.tur === "acilis" || h.tur === "gelis" ? cevir(h.not) : h.not) : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-ink-3">—</p>
        )}
      </div>

      <div className="mt-4">
        <label className={etiket}>{cevir("Used in")}</label>
        {d.kullanimlar.length ? (
          <ul className="space-y-1 text-sm">
            {d.kullanimlar.map((k) => (
              <li key={k.id} className={k.kaldirildiTs ? "text-ink-3" : undefined}>
                <span className="tnum">{tarih(k.ts)}</span> · {k.riserRn} · {cevir(k.etiket)} · {k.kim}
                {k.kaldirildiTs && ` ${cevir("(selection removed: {0})", kaldirmaNedeniMetni(k.kaldirmaNedeni))}`}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-ink-3">—</p>
        )}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <Buton tur="birincil" mesgul={mesgul} disabled={belgeMesgul} onClick={kaydet}>{cevir("Save")}</Buton>
        <Buton disabled={mesgul || belgeMesgul} onClick={onKapat}>{cevir("Cancel")}</Buton>
        {!p.bitti && (
          <Buton
            className="ml-auto"
            disabled={mesgul || belgeMesgul || kaydedilmemis}
            title={kaydedilmemis ? cevir("Save your changes first.") : undefined}
            onClick={onBitir}
          >
            {cevir("Mark finished")}
          </Buton>
        )}
      </div>
    </Modal>
  );
}

/** D-046 "Mark finished" onayı: kalan 0'a iner, geçmişe "finished" satırı yazılır; not isteğe bağlı. */
function BitirPenceresi({ id, pkg, onKapat, onBitti }: { id: string; pkg: string; onKapat: () => void; onBitti: () => void }) {
  const [not, setNot] = useState("");
  const [hata, setHata] = useState<string | null>(null);
  const [mesgul, setMesgul] = useState(false);
  const bitir = async () => {
    setHata(null);
    setMesgul(true);
    try {
      await api.post<PaketDetayi>(`/kutuk/stok/paket/${id}/bitti`, { not: not.trim() || null });
      onBitti();
    } catch (e) {
      setHata(hataMetni(e));
    } finally {
      setMesgul(false);
    }
  };
  return (
    <Modal baslik={cevir("Mark finished")} onKapat={onKapat} kilitli={mesgul}>
      <Hata mesaj={hata} />
      <p className="text-sm leading-relaxed text-ink-2">
        {cevir("Mark {0} finished? It will no longer be offered in triage or the part list; fit-up lists it under finished packages. Records that already use it keep it.", pkg)}
      </p>
      <div className="mt-3">
        <label className={etiket}>{cevir("Note (optional)")}</label>
        <textarea rows={2} className={alan} value={not} onChange={(e) => setNot(e.target.value)} />
      </div>
      <div className="mt-4 flex gap-2">
        <Buton tur="birincil" mesgul={mesgul} onClick={bitir}>{cevir("Mark finished")}</Buton>
        <Buton disabled={mesgul} onClick={onKapat}>{cevir("Cancel")}</Buton>
      </div>
    </Modal>
  );
}

/**
 * D-053 "Clear opening stock" onayı: PN'in pakete girmemiş açılış stoğu 0 olur, satırdaki açılış metni ve bağlantı kaybolur. Paket
 * açılmaz; geri alınamaz (önceki miktar olay kaydında durur). `miktar`: birimiyle gösterim ("150 ad").
 */
function AcilisSifirlaPenceresi({ pn, miktar, onKapat, onSifirlandi }: { pn: string; miktar: string; onKapat: () => void; onSifirlandi: () => void }) {
  const [hata, setHata] = useState<string | null>(null);
  const [mesgul, setMesgul] = useState(false);
  const sifirla = async () => {
    setHata(null);
    setMesgul(true);
    try {
      await api.post(`/kutuk/stok/${encodeURIComponent(pn)}/acilis-sifirla`);
      onSifirlandi();
    } catch (e) {
      setHata(hataMetni(e));
    } finally {
      setMesgul(false);
    }
  };
  return (
    <Modal baslik={cevir("Clear opening stock — {0}", pn)} onKapat={onKapat} kilitli={mesgul}>
      <Hata mesaj={hata} />
      <p className="text-sm leading-relaxed text-ink-2">
        {cevir("Clear the opening stock of {0}? {1} entered before packages will no longer be counted. No package is created; enter the real packages with + Add package. This cannot be undone.", pn, miktar)}
      </p>
      <div className="mt-4 flex gap-2">
        <Buton tur="birincil" mesgul={mesgul} onClick={sifirla}>{cevir("Clear opening stock")}</Buton>
        <Buton disabled={mesgul} onClick={onKapat}>{cevir("Cancel")}</Buton>
      </div>
    </Modal>
  );
}

/** "Stock count": yalnız aktif paketler, PN başlıkları altında; boş satır sayılmamış sayılır, 0 paketi bitirir. */
function SayimPenceresi({ pnler, onKapat, onKaydedildi }: { pnler: StokPnSatiri[]; onKapat: () => void; onKaydedildi: () => void }) {
  const [girdiler, setGirdiler] = useState<Record<string, { sayilan: string; not: string }>>({});
  const [hata, setHata] = useState<string | null>(null);
  const [mesgul, setMesgul] = useState(false);
  const gruplar = pnler.map((p) => ({ p, paketler: p.paketler.filter((k) => !k.bitti) })).filter((g) => g.paketler.length > 0);
  const hucre = "px-3 py-2 align-middle text-sm";
  const yaz = (id: string, d: Partial<{ sayilan: string; not: string }>) =>
    setGirdiler((g) => ({ ...g, [id]: { sayilan: g[id]?.sayilan ?? "", not: g[id]?.not ?? "", ...d } }));

  const kaydet = async () => {
    setHata(null);
    setMesgul(true);
    try {
      const satirlar = Object.entries(girdiler)
        .filter(([, v]) => v.sayilan.trim() !== "")
        .map(([paketId, v]) => ({ paketId, sayilan: sayiOku(v.sayilan), not: v.not.trim() || null }));
      await api.post("/kutuk/stok/sayim", { satirlar });
      onKaydedildi();
    } catch (e) {
      setHata(hataMetni(e));
    } finally {
      setMesgul(false);
    }
  };

  return (
    <Modal baslik={cevir("Stock count")} onKapat={onKapat} genis="cok" kilitli={mesgul}>
      <Hata mesaj={hata} />
      {gruplar.length > 0 && (
        <div className="scroll-thin overflow-x-auto">
          <table className="w-full min-w-[640px]">
            <thead>
              <tr className="border-b border-line">
                {["Package", "Heat / SN / lot", "PO no.", "System quantity", "Counted", "Unit", "Difference", "Note"].map((h) => <th key={h} className={tabloBas}>{cevir(h)}</th>)}
              </tr>
            </thead>
            <tbody>
              {gruplar.map(({ p, paketler }) => (
                <Fragment key={p.pn}>
                  <tr className="border-b border-line bg-surface-2">
                    <td colSpan={8} className="px-3 py-2 text-sm">
                      <span className="font-mono font-semibold">{p.pn}</span> <span className="text-ink-2">{p.tanim}</span>
                    </td>
                  </tr>
                  {paketler.map((k) => {
                    const g = girdiler[k.id] ?? { sayilan: "", not: "" };
                    const sayilan = sayiOku(g.sayilan);
                    const gecerli = sayilan !== null && !Number.isNaN(sayilan);
                    const fark = gecerli ? yuvarla3(sayilan - k.miktar) : null;
                    const alt = gelisMetni(k);
                    return (
                      <tr key={k.id} className="border-b border-line last:border-0">
                        <td className={hucre}>
                          <span className="block font-mono font-semibold">{k.pkg}</span>
                          {alt && <span className="block text-xs text-ink-3">{alt}</span>}
                        </td>
                        <td className={hucre}>{k.iz || bos}</td>
                        <td className={cn(hucre, "whitespace-nowrap")}>{k.poNo || bos}</td>
                        <td className={cn(hucre, "tnum")}>{miktarMetni(k.miktar)}</td>
                        <td className={hucre}>
                          <input className={cn(girdi, "w-28")} type="number" min={0} step="any" value={g.sayilan} onChange={(e) => yaz(k.id, { sayilan: e.target.value })} />
                        </td>
                        <td className={hucre}>{p.birim}</td>
                        <td className={hucre}>
                          {fark === null ? (
                            bos
                          ) : (
                            <span className="whitespace-nowrap">
                              <span className={cn("tnum", fark !== 0 && "text-warn")}>{farkMetni(fark)}</span>
                              {sayilan === 0 && <> <span className="text-xs text-ink-3">{cevir("→ finished")}</span></>}
                            </span>
                          )}
                        </td>
                        <td className={hucre}>
                          <input className={girdi} placeholder={cevir("optional")} value={g.not} onChange={(e) => yaz(k.id, { not: e.target.value })} />
                        </td>
                      </tr>
                    );
                  })}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="mt-3 text-xs text-ink-3">{cevir("Leave blank if not counted. 0 marks the package finished.")}</p>
      <p className="mt-1 text-xs text-ink-3">{cevir("Stock found without a package? Add it with + Add package.")}</p>
      <div className="mt-4 flex gap-2">
        <Buton tur="birincil" mesgul={mesgul} onClick={kaydet}>{cevir("Save")}</Buton>
        <Buton disabled={mesgul} onClick={onKapat}>{cevir("Cancel")}</Buton>
      </div>
    </Modal>
  );
}
